import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

function StockStatusTable({ type, id, onRaisePO }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasShortages, setHasShortages] = useState(false);
  const [shortageCount, setShortageCount] = useState(0);
  
  useEffect(() => {
    if (id) {
      fetchStockStatus();
    }
  }, [id, type]);
  
  const fetchStockStatus = async () => {
    try {
      const endpoint = type === 'job' 
        ? `/api/stock-check/job/${id}`
        : `/api/stock-check/sales-order/${id}`;
        
      const response = await apiFetch(endpoint);
      const data = await response.json();
      
      setItems(data.items || []);
      setHasShortages(data.has_shortages || false);
      setShortageCount(data.shortage_count || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stock status:', error);
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_stock':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            ✅ In Stock
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
            ⚠️ Low Stock
          </span>
        );
      case 'out_of_stock':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
            ❌ Out of Stock
          </span>
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (items.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <p className="text-gray-600">No {type === 'job' ? 'materials' : 'products'} assigned yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Shortage Banner */}
      {hasShortages && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-red-800">
                  Stock Shortage: {shortageCount} item{shortageCount !== 1 ? 's' : ''} out of stock or low
                </p>
              </div>
            </div>
            {onRaisePO && (
              <button
                onClick={() => onRaisePO(items.filter(item => item.has_shortage))}
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 text-sm"
              >
                Raise Purchase Order
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Stock Status Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Supplier
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Needed
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Available
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Shortage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {item.product_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {item.product_code}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {item.supplier_name || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">
                  {parseFloat(item.needed).toFixed(2)}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <span className={item.available >= item.needed ? 'text-green-700 font-semibold' : 'text-gray-900'}>
                    {parseFloat(item.available).toFixed(2)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  {item.shortage > 0 ? (
                    <span className="text-red-700 font-semibold">
                      {parseFloat(item.shortage).toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm">
                  {getStatusBadge(item.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-500">In Stock</p>
            <p className="text-2xl font-bold text-green-700">
              {items.filter(item => item.status === 'in_stock').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Low Stock</p>
            <p className="text-2xl font-bold text-amber-600">
              {items.filter(item => item.status === 'partial').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Out of Stock</p>
            <p className="text-2xl font-bold text-red-700">
              {items.filter(item => item.status === 'out_of_stock').length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockStatusTable;
